<template>
  <VcModal is-mobile-fullscreen max-width="60rem">
    <div v-if="psProduct && psProduct.products && psProduct.products.length > 0" class="ps-in-food-modal">
      <div class="main-header">
        <VcTypography tag="h2" class="uppercase text-4xl">{{ $t("custom.ps_in_food.main_title") }}</VcTypography>
        <VcTypography class="product-info" tag="h2">{{ psProduct.products[0].productInfo?.name?.value }}</VcTypography>
      </div>
      <div class="info-header">
        <VcTypography tag="h2">{{ $t("custom.ps_in_food.description_title") }}</VcTypography>
        <dl>
          <dt>{{ $t("custom.ps_in_food.ean") }}</dt>
          <dd class="mark">{{ psProduct.products[0].summary?.ean }}</dd>
        </dl>
      </div>
      <div class="main-description">
        <p>{{ psProduct.products[0].commercialInfo?.commercialStoryText?.value }}</p>
      </div>
      <div class="additional-description">
        <div>
          <VcTypography tag="h2">{{ $t("custom.ps_in_food.origin_title") }}</VcTypography>
          <dl>
            <dt>{{ $t("custom.ps_in_food.country_of_origin_name") }}</dt>
            <dd>{{ psProduct.products[0].productInfo?.countryOfOriginName?.value }}</dd>
            <dt>{{ $t("custom.ps_in_food.legal_name") }}</dt>
            <dd>{{ psProduct.products[0].commercialInfo?.legalName?.value }}</dd>
          </dl>
        </div>
        <div>
          <VcTypography tag="h2">{{ $t("custom.ps_in_food.ingredients_title") }}</VcTypography>
          <p>{{ psProduct.products[0].specificationInfo?.ingredientSet?.ingredientDeclaration?.value }}</p>
        </div>
      </div>
      <VcTypography tag="h2">{{ $t("custom.ps_in_food.allergens_title") }}</VcTypography>
      <ul class="labels">
        <li>
          {{ $t("custom.ps_in_food.allergens_contains") }}
          <span :class="containsClass">{{ psInFoodAllergenLabel.Contains }}</span>
        </li>
        <li>
          {{ $t("custom.ps_in_food.allergens_may_contain") }}
          <span :class="containsClass">{{ psInFoodAllergenLabel.MayContain }}</span>
        </li>
        <li>
          {{ $t("custom.ps_in_food.allergens_not_supplied") }}
          <span :class="notContainsClass">{{ psInFoodAllergenLabel.DoesNotContain }}</span>
        </li>
        <li>
          {{ $t("custom.ps_in_food.allergens_not_contain") }}
          <span :class="notContainsClass">{{ psInFoodAllergenLabel.NotSpecified }}</span>
        </li>
      </ul>
      <div class="allegen-set">
        <div
          v-for="item in psProduct.products[0].specificationInfo?.allergenSet?.allergens"
          :key="item.id"
          class="allegen-item"
        >
          <div v-if="item.name && item.name.value">{{ item.name.value }}</div>
          <div :class="getPsInFoodAllergen(item.levelOfContainmentId).class">
            {{ getPsInFoodAllergen(item.levelOfContainmentId).label }}
          </div>
        </div>
      </div>
    </div>
  </VcModal>
</template>

<script setup lang="ts">
import { usePsInFoodProduct } from "@/shared/catalog";
import type { ProductSearchResultType } from "@/core/api/graphql/types";
import type { PropType } from "vue";

defineProps({
  psProduct: {
    type: Object as PropType<ProductSearchResultType>,
    required: false,
  },
});

const { psInFoodAllergenLabel, getPsInFoodAllergen, containsClass, notContainsClass } = usePsInFoodProduct();
</script>

<style lang="scss">
.ps-in-food-modal {
  margin: 0 auto;
  padding: 1.5rem;

  .main-header,
  .info-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .main-header {
    margin: 0 0 2rem;
  }

  h2 {
    margin: 0 0 1rem;
  }

  .product-info {
    @apply text-[--color-secondary-500];
  }

  .info-header {
    margin: 0 0 1rem;

    h3 {
      margin-bottom: 1rem;
    }

    dl {
      margin: 0;
      font-weight: bold;

      dt,
      dd {
        display: inline-flex;
        margin: 0;
      }

      dd {
        margin-right: 1rem;
        margin-left: 0.375rem;
      }

      .mark {
        color: #3aae2a;
      }
    }
  }

  .main-description {
    margin: 0 0 2rem;

    p {
      margin: 0;

      & + p {
        margin-top: 1rem;
      }
    }
  }

  .additional-description {
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: 1fr;
    margin: 0 0 2rem;

    p {
      margin: 0;
    }

    dl {
      margin: 0;
      overflow: hidden;

      dt,
      dd {
        margin: 0;
      }

      dt {
        margin-right: 0.375rem;
        float: left;
      }
    }
  }

  .labels {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;

    li {
      display: flex;
      align-items: center;
      height: 2.25rem;
      margin: 0 1rem 1rem 0;
      padding: 0 0.5rem;
      background: #f5f5f5;
      border: 1px solid #b4b4b4;
      border-radius: 3px;

      span {
        display: inline-block;
        margin: 0 0 0 1rem;
      }
    }
  }

  .contains,
  .not-contains {
    font-weight: bold;
  }

  .contains {
    color: #ce3037;
  }

  .not-contains {
    color: #0b4c3d;
  }

  .allegen-set {
    display: grid;
    grid-template-columns: 1fr;

    .allegen-item {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      border-bottom: 1px solid #707070;
      padding: 0.75rem 0;

      &:nth-child(odd),
      &:nth-child(even) {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }

  @media screen and (min-width: 1024px) {
    .main-header,
    .info-header {
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
    }

    h2 {
      margin-bottom: 0;
    }

    .main-header {
      margin-bottom: 3.6rem;
    }

    .info-header {
      h3 {
        margin-bottom: 0;
      }
    }

    .additional-description {
      grid-template-columns: 1fr 1fr;
    }

    .allegen-set {
      grid-template-columns: 1fr 1fr;

      .allegen-item {
        &:nth-child(odd) {
          padding-right: 4.6em;
        }

        &:nth-child(even) {
          padding-left: 4.6em;
        }
      }
    }
  }
}
</style>
